import * as React from "react";
import { ReactFlow, Background, BackgroundVariant, type Node, type Edge } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import nodeTypes from "./index";
import "@flows/canvas/colors.css";
import type { StoryMeta } from "@features/devtools/registry/types";
import { Cpu, Box, Settings, Rocket, AlertTriangle } from 'lucide-react';
import { useRuntimeStore } from '@features/flows/runtime/useRuntimeStore';

type PinsSpec = {
  execIn?: string[];
  execOut?: string[];
  dataIn?: Array<{ id: string; type?: string; label?: string }>;
  dataOut?: Array<{ id: string; type?: string; label?: string }>;
};

type PreviewProps = {
  label: string;
  pins?: PinsSpec;
  status?: 'idle'|'running'|'waiting'|'executed'|'error';
  startedAt?: number;
  finishedAt?: number;
  durationMs?: number;
  icon?: React.ReactNode;
  error?: { message?: string } | string;
  // Streaming demo controls (single-node)
  simulateLive?: boolean;
  liveText?: string;
  liveSpeedMs?: number;
};

function NodePreview({
  label, pins, status, startedAt, finishedAt, durationMs, icon, error,
  simulateLive, liveText, liveSpeedMs = 40,
}: PreviewProps) {
  const p = pins ?? {};

  const baseNode = React.useMemo<Node>(() => ({
    id: "n1",
    type: "default",
    position: { x: 60, y: 60 },
    data: {
      label,
      icon: icon ?? <Cpu className="size-3 text-muted-foreground" />,
      pins: {
        exec: {
          in: ((p.execIn) ?? ["in"]).map((id) => ({ id })),
          out: ((p.execOut) ?? ["next"]).map((id) => ({ id })),
        },
        data: {
          in: (p.dataIn) ?? [{ id: "value", type: "number" }],
          out: (p.dataOut) ?? [{ id: "value", type: "number" }],
        },
      },
      liveDemo: undefined,
    },
  }), [label, icon, JSON.stringify(pins)]);

  const [nodes, setNodes] = React.useState<Node[]>([baseNode]);
  const edges: Edge[] = [];

  const setGraph = useRuntimeStore((s) => s.setGraph);
  const resetRun = useRuntimeStore((s) => s.resetRun);
  const apply = useRuntimeStore((s) => s.applyTelemetry);

  React.useEffect(() => {
    // Initialize graph index and a fresh run envelope for story
    setGraph(nodes as any, []);
    resetRun('story-run', 'story-flow');

    const nid = 'n1';
    const now = Date.now();
    const startTs = (typeof startedAt === 'number' ? startedAt : (typeof durationMs === 'number' ? now - Math.max(0, durationMs) : undefined));
    const endTs = (typeof finishedAt === 'number' ? finishedAt : now);
    const errMsg = typeof error === 'string' ? error : (error?.message ?? undefined);

    switch (status) {
      case 'running': {
        apply({ action: 'start', nodeId: nid, ts: startTs ?? now } as any);
        break;
      }
      case 'waiting': {
        apply({ action: 'wait', nodeId: nid, ts: now } as any);
        break;
      }
      case 'executed': {
        if (startTs) apply({ action: 'start', nodeId: nid, ts: startTs } as any);
        apply({ action: 'complete', nodeId: nid, ts: endTs } as any);
        break;
      }
      case 'error': {
        if (startTs) apply({ action: 'start', nodeId: nid, ts: startTs } as any);
        apply({ action: 'error', nodeId: nid, ts: endTs, payload: { error: { message: errMsg || 'Story error' } } } as any);
        break;
      }
      case 'idle':
      default: {
        // Do nothing
        break;
      }
    }
  }, [label, JSON.stringify(pins), status, startedAt, finishedAt, durationMs, (typeof error === 'string' ? error : error?.message), setGraph, resetRun, apply]);

  // Reset bubble and node state when simulateLive is turned off (e.g., Reset button)
  React.useEffect(() => {
    if (!simulateLive) {
      setNodes([baseNode]);
    }
  }, [simulateLive, baseNode]);

  // --- Streaming simulation (single or multiple) ---
  React.useEffect(() => {
    const timers: number[] = [];
    const startStreamFor = (nodeId: string, text: string, speed: number) => {
      const base = String(text || '');
      const tokens = base.split(/(\s+)/).filter(Boolean);
      if (!tokens.length) return;
      let i = 0;
      const tick = () => {
        setNodes((prev) =>
          prev.map((n) =>
            n.id === nodeId
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    liveDemo: {
                      text: (n.data as any).liveDemo?.text
                        ? String((n.data as any).liveDemo.text) + tokens[i]
                        : tokens[i],
                      done: i >= tokens.length - 1,
                    },
                  },
                }
              : n
          )
        );
        i += 1;
        if (i >= tokens.length) {
          // Mark node as completed in runtime store so status turns green
          try { apply({ action: 'complete', nodeId, ts: Date.now() } as any); } catch {}
          return;
        }
        timers.push(window.setTimeout(tick, speed));
      };
      timers.push(window.setTimeout(tick, speed));
    };

    if (simulateLive && liveText) {
      startStreamFor('n1', liveText, liveSpeedMs);
    }
    return () => { timers.forEach((t) => clearTimeout(t)); };
  }, [simulateLive, liveText, liveSpeedMs, apply]);
  return (
    <div className="h-[720px] border rounded">
      <ReactFlow<Node>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        panOnScroll
        panOnDrag
        zoomOnScroll
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} />
      </ReactFlow>
    </div>
  );
}

type Props = React.ComponentProps<typeof NodePreview>;

const meta: StoryMeta<Props> = {
  id: "flows/CustomNode",
  title: "CustomNode",
  group: "Flows/Nodes",
  component: NodePreview as any,
  description: "Compact flow node with typed data/exec pins.",
  defaultProps: {
    label: "Increment",
    icon: <Cpu className="size-3 text-muted-foreground" />,
    pins: {
      execIn: ["in"],
      execOut: ["next"],
      dataIn: [{ id: "value", type: "number" }],
      dataOut: [{ id: "value", type: "number" }],
    },
    status: 'executed',
    durationMs: 128,
  },
  controls: {
    label: { type: "text", label: "Label" },
    pins: { type: "json", label: "Pins (JSON)" },
    status: { type: 'select', label: 'Status', options: ['idle','running','waiting','executed','error'] },
    durationMs: { type: 'number', label: 'Duration (ms)' },
    error: { type: 'text', label: 'Error message' },
    simulateLive: { type: 'boolean', label: 'Simulate Live' },
    liveText: { type: 'text', label: 'Live Text' },
    liveSpeedMs: { type: 'number', label: 'Live Speed (ms/chunk)' },
  },
  variants: [
    {
      name: "Waiting",
      props: {
        label: "Waiting Node",
        icon: <Settings className="size-3 text-muted-foreground" />,
        status: 'waiting',
        pins: {
          execIn: ["in"],
          execOut: ["next"],
          dataIn: [
            { id: "payload", type: "object", label: "object" },
          ],
          dataOut: [],
        },
      },
    },
    {
      name: "Minimal",
      props: { label: "Node", icon: <Box className="size-3 text-muted-foreground" />, pins: { execIn: ["in"], execOut: ["next"], dataIn: [], dataOut: [] } },
    },
    {
      name: "Running",
      props: {
        label: "Working…",
        icon: <Settings className="size-3 text-muted-foreground" />,
        status: 'running',
        startedAt: Date.now(),
        pins: {
          execIn: ["in"],
          execOut: ["next"],
          dataIn: [
            { id: "input", type: "string", label: "string" },
          ],
          dataOut: [],
        },
      },
    },
    {
      name: "With Data",
      props: {
        label: "Transformer",
        icon: <Settings className="size-3 text-muted-foreground" />,
        pins: {
          execIn: ["in"],
          execOut: ["next"],
          dataIn: [
            { id: "input", type: "string" },
            { id: "count", type: "number" },
          ],
          dataOut: [{ id: "result", type: "object" }],
        },
      },
    },
    {
      name: "Error",
      props: {
        label: "Failed Node",
        icon: <AlertTriangle className="size-3 text-muted-foreground" />,
        status: 'error',
        error: { message: 'E_PIN_SCHEMA_INPUT: core.number.add@1.0.0' },
        pins: {
          execIn: ["in"],
          execOut: ["next"],
          dataIn: [
            { id: "payload", type: "object", label: "object" },
          ],
          dataOut: [],
        },
      },
    },
    {
      name: "Streaming (single)",
      props: {
        label: "LLM Writer",
        icon: <Settings className="size-3 text-muted-foreground" />,
        status: 'running',
        simulateLive: true,
        liveSpeedMs: 40,
        liveText:
          "We are streaming a longer paragraph to preview how the inline bubble behaves under continuous output. The content arrives token by token, but the UI should remain calm, compact, and legible. As the text grows, the bubble only shows the most recent tail while a full view is available in the popover. This mirrors a typical assistant-style response where sentences unfold progressively. The goal is to validate spacing, contrast, and rhythm without overwhelming the canvas. When the stream completes, the node marks as executed and the indicator turns green.",
      },
    },
    {
      name: "All Types",
      props: {
        label: "Types Palette",
        icon: <Rocket className="size-3 text-muted-foreground" />,
        pins: {
          execIn: ["in"],
          execOut: ["next"],
          dataIn: [
            { id: "string", type: "string", label: "string" },
            { id: "number", type: "number", label: "number" },
            { id: "boolean", type: "boolean", label: "boolean" },
            { id: "object", type: "object", label: "object" },
            { id: "array", type: "array", label: "array" },
            { id: "any", type: "any", label: "any" },
          ],
          dataOut: [
            { id: "string", type: "string", label: "string" },
            { id: "number", type: "number", label: "number" },
            { id: "boolean", type: "boolean", label: "boolean" },
            { id: "object", type: "object", label: "object" },
            { id: "array", type: "array", label: "array" },
            { id: "any", type: "any", label: "any" },
          ],
        },
      },
    },
  ],
};

export default meta;
